import { supabase } from './supabase';
import type { ProductTemplate, TemplateShape, ShapeProcessSettings } from '@jigflow/shared';

export async function getTemplateByProductId(
  productId: string,
  variationId?: string,
  machineId?: string
): Promise<ProductTemplate | null> {
  let query = supabase
    .from('product_templates')
    .select('*')
    .eq('product_id', productId);

  if (variationId) {
    query = query.eq('variation_id', variationId);
  } else {
    query = query.is('variation_id', null);
  }

  if (machineId) {
    query = query.eq('machine_id', machineId);
  }

  // Use limit(1) instead of maybeSingle() to handle duplicate templates gracefully
  const { data, error } = await query.limit(1);

  if (error || !data || data.length === 0) return null;

  const row = data[0];
  return {
    id: row.id,
    productId: row.product_id,
    variationId: row.variation_id || undefined,
    machineId: row.machine_id,
    name: row.name,
    svgContent: row.svg_content || undefined,
    width: parseFloat(row.width),
    height: parseFloat(row.height),
  };
}

export async function getShapesByTemplateId(templateId: string): Promise<TemplateShape[]> {
  const { data, error } = await supabase
    .from('template_shapes')
    .select('*')
    .eq('template_id', templateId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => {
    const shape: TemplateShape = {
      id: row.id,
      templateId: row.template_id,
      fieldId: row.field_id || undefined,
      type: row.type,
      x: parseFloat(row.x),
      y: parseFloat(row.y),
      width: parseFloat(row.width),
      height: parseFloat(row.height),
      rotation: parseFloat(row.rotation),
      orderIndex: row.order_index,
      svgData: row.svg_data || undefined,
      score: row.score || false,
      engrave: row.engrave || false,
      cut: row.cut || false,
      scoreColor: row.score_color || undefined,
      engraveColor: row.engrave_color || undefined,
      cutColor: row.cut_color || undefined,
      lbrnCutIndex: row.lbrn_cut_index || undefined,
    };

    if (row.raster_data) shape.rasterData = row.raster_data;
    if (row.raster_format) shape.rasterFormat = row.raster_format;
    if (row.raster_original_width) shape.rasterOriginalWidth = parseFloat(row.raster_original_width);
    if (row.raster_original_height) shape.rasterOriginalHeight = parseFloat(row.raster_original_height);
    if (row.original_xcs_data) shape.originalXcsData = row.original_xcs_data;

    return shape;
  });
}

export async function getProcessSettingsByShapeId(shapeId: string): Promise<ShapeProcessSettings[]> {
  const { data, error } = await supabase
    .from('shape_process_settings')
    .select('*')
    .eq('shape_id', shapeId);

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => {
    const settings: ShapeProcessSettings = {
      id: row.id,
      shapeId: row.shape_id,
      operation: row.operation,
      outputEnabled: row.output_enabled || false,
      power: row.power ? parseFloat(row.power) : undefined,
      speed: row.speed ? parseFloat(row.speed) : undefined,
      passes: row.passes || 1,
      kerfEnabled: row.kerf_enabled || false,
      kerfDistance: row.kerf_distance ? parseFloat(row.kerf_distance) : undefined,
      density: row.density ? parseFloat(row.density) : undefined,
      scanMode: row.scan_mode || undefined,
      lowerFocusEnabled: row.lower_focus_enabled || false,
      lowerFocusValue: row.lower_focus_value as 1 | 2 | undefined,
      lightSource: row.light_source || undefined,
    };

    if (row.bitmap_engrave_mode) settings.bitmapEngraveMode = row.bitmap_engrave_mode;
    if (row.dot_duration) settings.dotDuration = parseFloat(row.dot_duration);
    if (row.enable_break_point !== undefined) settings.enableBreakPoint = row.enable_break_point;
    if (row.break_point_size) settings.breakPointSize = parseFloat(row.break_point_size);
    if (row.break_point_count) settings.breakPointCount = row.break_point_count;
    if (row.break_point_mode) settings.breakPointMode = row.break_point_mode;
    if (row.break_point_distance) settings.breakPointDistance = parseFloat(row.break_point_distance);
    if (row.break_point_power) settings.breakPointPower = parseFloat(row.break_point_power);

    if (row.z_offset) settings.zOffset = parseFloat(row.z_offset);
    if (row.z_per_pass) settings.zPerPass = parseFloat(row.z_per_pass);
    if (row.frequency) settings.frequency = parseFloat(row.frequency);
    if (row.override_frequency !== undefined) settings.overrideFrequency = row.override_frequency;
    if (row.ppi) settings.ppi = parseFloat(row.ppi);
    if (row.enable_ppi !== undefined) settings.enablePpi = row.enable_ppi;
    if (row.enable_laser1 !== undefined) settings.enableLaser1 = row.enable_laser1;
    if (row.enable_laser2 !== undefined) settings.enableLaser2 = row.enable_laser2;
    if (row.start_delay) settings.startDelay = parseFloat(row.start_delay);
    if (row.end_delay) settings.endDelay = parseFloat(row.end_delay);
    if (row.air_assist !== undefined) settings.airAssist = row.air_assist;
    if (row.blower_speed_percent) settings.blowerSpeedPercent = parseFloat(row.blower_speed_percent);
    if (row.enable_cut_through !== undefined) settings.enableCutThrough = row.enable_cut_through;
    if (row.cut_through_power) settings.cutThroughPower = parseFloat(row.cut_through_power);
    if (row.overcut) settings.overcut = parseFloat(row.overcut);
    if (row.ramp_length) settings.rampLength = parseFloat(row.ramp_length);
    if (row.enable_perforation !== undefined) settings.enablePerforation = row.enable_perforation;
    if (row.perforation_cut_length) settings.perforationCutLength = parseFloat(row.perforation_cut_length);
    if (row.perforation_skip_length) settings.perforationSkipLength = parseFloat(row.perforation_skip_length);
    if (row.enable_dot_mode !== undefined) settings.enableDotMode = row.enable_dot_mode;
    if (row.dot_time) settings.dotTime = parseFloat(row.dot_time);
    if (row.dot_spacing) settings.dotSpacing = parseFloat(row.dot_spacing);
    if (row.lead_in_type) settings.leadInType = row.lead_in_type;
    if (row.lead_in_length) settings.leadInLength = parseFloat(row.lead_in_length);
    if (row.lead_in_angle) settings.leadInAngle = parseFloat(row.lead_in_angle);
    if (row.lead_out_type) settings.leadOutType = row.lead_out_type;
    if (row.lead_out_length) settings.leadOutLength = parseFloat(row.lead_out_length);
    if (row.lead_out_angle) settings.leadOutAngle = parseFloat(row.lead_out_angle);
    if (row.scan_angle) settings.scanAngle = parseFloat(row.scan_angle);
    if (row.cross_hatch !== undefined) settings.crossHatch = row.cross_hatch;
    if (row.flood_fill !== undefined) settings.floodFill = row.flood_fill;
    if (row.overscan_percent) settings.overscanPercent = parseFloat(row.overscan_percent);
    if (row.hide !== undefined) settings.hide = row.hide;
    if (row.lbrn_priority) settings.lbrnPriority = parseFloat(row.lbrn_priority);

    return settings;
  });
}

export async function getProcessSetting(
  shapeId: string,
  operation: 'score' | 'engrave' | 'cut'
): Promise<ShapeProcessSettings | null> {
  const { data, error } = await supabase
    .from('shape_process_settings')
    .select('*')
    .eq('shape_id', shapeId)
    .eq('operation', operation)
    .maybeSingle();

  if (error || !data) return null;

  const settings: ShapeProcessSettings = {
    id: data.id,
    shapeId: data.shape_id,
    operation: data.operation,
    outputEnabled: data.output_enabled || false,
    power: data.power ? parseFloat(data.power) : undefined,
    speed: data.speed ? parseFloat(data.speed) : undefined,
    passes: data.passes || 1,
    kerfEnabled: data.kerf_enabled || false,
    kerfDistance: data.kerf_distance ? parseFloat(data.kerf_distance) : undefined,
    density: data.density ? parseFloat(data.density) : undefined,
    scanMode: data.scan_mode || undefined,
    lowerFocusEnabled: data.lower_focus_enabled || false,
    lowerFocusValue: data.lower_focus_value as 1 | 2 | undefined,
    lightSource: data.light_source || undefined,
  };

  return settings;
}

export async function getShapesWithSettings(shapeIds: string[]): Promise<Set<string>> {
  if (shapeIds.length === 0) return new Set();

  const { data, error } = await supabase
    .from('shape_process_settings')
    .select('shape_id')
    .in('shape_id', shapeIds);

  if (error) throw error;
  if (!data) return new Set();

  return new Set(data.map((row) => row.shape_id));
}

export async function getOutputEnabledMap(
  shapeIds: string[]
): Promise<Map<string, Map<string, boolean>>> {
  if (shapeIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from('shape_process_settings')
    .select('shape_id, operation, output_enabled')
    .in('shape_id', shapeIds);

  if (error) throw error;
  if (!data) return new Map();

  const map = new Map<string, Map<string, boolean>>();

  for (const row of data) {
    if (!map.has(row.shape_id)) {
      map.set(row.shape_id, new Map());
    }
    map.get(row.shape_id)!.set(row.operation, row.output_enabled || false);
  }

  return map;
}

export async function saveTemplate(
  workspaceId: string,
  template: Partial<ProductTemplate>
): Promise<ProductTemplate> {
  if (template.id) {
    const { data, error } = await supabase
      .from('product_templates')
      .update({
        name: template.name,
        svg_content: template.svgContent,
        width: template.width,
        height: template.height,
      })
      .eq('id', template.id)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      productId: data.product_id,
      variationId: data.variation_id || undefined,
      machineId: data.machine_id,
      name: data.name,
      svgContent: data.svg_content || undefined,
      width: parseFloat(data.width),
      height: parseFloat(data.height),
    };
  } else {
    const { data, error } = await supabase
      .from('product_templates')
      .insert({
        workspace_id: workspaceId,
        product_id: template.productId!,
        variation_id: template.variationId,
        machine_id: template.machineId!,
        name: template.name!,
        svg_content: template.svgContent,
        width: template.width!,
        height: template.height!,
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      productId: data.product_id,
      variationId: data.variation_id || undefined,
      machineId: data.machine_id,
      name: data.name,
      svgContent: data.svg_content || undefined,
      width: parseFloat(data.width),
      height: parseFloat(data.height),
    };
  }
}

export async function saveShape(
  workspaceId: string,
  shape: Partial<TemplateShape>
): Promise<TemplateShape> {
  // Use upsert so client-generated UUIDs work for both new and existing shapes
  const payload: Record<string, unknown> = {
    workspace_id: workspaceId,
    template_id: shape.templateId,
    field_id: shape.fieldId,
    type: shape.type,
    x: shape.x,
    y: shape.y,
    width: shape.width,
    height: shape.height,
    rotation: shape.rotation,
    order_index: shape.orderIndex,
    svg_data: shape.svgData,
    score: shape.score,
    engrave: shape.engrave,
    cut: shape.cut,
    score_color: shape.scoreColor,
    engrave_color: shape.engraveColor,
    cut_color: shape.cutColor,
    lbrn_cut_index: shape.lbrnCutIndex,
    raster_data: shape.rasterData,
    raster_format: shape.rasterFormat,
    raster_original_width: shape.rasterOriginalWidth,
    raster_original_height: shape.rasterOriginalHeight,
    original_xcs_data: shape.originalXcsData,
  };

  if (shape.id) {
    payload.id = shape.id;
  }

  const { data, error } = await supabase
    .from('template_shapes')
    .upsert(payload)
    .select()
    .single();

  if (error) throw error;

  return mapShapeFromDb(data);
}

export async function saveProcessSettings(
  workspaceId: string,
  settings: Partial<ShapeProcessSettings>
): Promise<ShapeProcessSettings> {
  // Use upsert so client-generated UUIDs work for both new and existing settings
  const payload: Record<string, unknown> = {
    workspace_id: workspaceId,
    ...mapSettingsToDb(settings),
  };

  if (settings.id) {
    payload.id = settings.id;
  }

  const { data, error } = await supabase
    .from('shape_process_settings')
    .upsert(payload)
    .select()
    .single();

  if (error) throw error;

  return mapSettingsFromDb(data);
}

export async function deleteShape(shapeId: string): Promise<void> {
  const { error } = await supabase
    .from('template_shapes')
    .delete()
    .eq('id', shapeId);
  if (error) throw error;
}

export async function deleteProcessSettings(settingId: string): Promise<void> {
  const { error } = await supabase
    .from('shape_process_settings')
    .delete()
    .eq('id', settingId);
  if (error) throw error;
}

export async function deleteTemplate(templateId: string): Promise<void> {
  const { error } = await supabase
    .from('product_templates')
    .delete()
    .eq('id', templateId);
  if (error) throw error;
}

function mapShapeFromDb(data: any): TemplateShape {
  const shape: TemplateShape = {
    id: data.id,
    templateId: data.template_id,
    fieldId: data.field_id || undefined,
    type: data.type,
    x: parseFloat(data.x),
    y: parseFloat(data.y),
    width: parseFloat(data.width),
    height: parseFloat(data.height),
    rotation: parseFloat(data.rotation),
    orderIndex: data.order_index,
    svgData: data.svg_data || undefined,
    score: data.score || false,
    engrave: data.engrave || false,
    cut: data.cut || false,
    scoreColor: data.score_color || undefined,
    engraveColor: data.engrave_color || undefined,
    cutColor: data.cut_color || undefined,
    lbrnCutIndex: data.lbrn_cut_index || undefined,
  };

  if (data.raster_data) shape.rasterData = data.raster_data;
  if (data.raster_format) shape.rasterFormat = data.raster_format;
  if (data.raster_original_width) shape.rasterOriginalWidth = parseFloat(data.raster_original_width);
  if (data.raster_original_height) shape.rasterOriginalHeight = parseFloat(data.raster_original_height);
  if (data.original_xcs_data) shape.originalXcsData = data.original_xcs_data;

  return shape;
}

function mapSettingsToDb(settings: Partial<ShapeProcessSettings>): any {
  const dbData: any = {};

  if (settings.shapeId) dbData.shape_id = settings.shapeId;
  if (settings.operation) dbData.operation = settings.operation;
  if (settings.outputEnabled !== undefined) dbData.output_enabled = settings.outputEnabled;
  if (settings.power !== undefined) dbData.power = settings.power;
  if (settings.speed !== undefined) dbData.speed = settings.speed;
  if (settings.passes !== undefined) dbData.passes = settings.passes;
  if (settings.kerfEnabled !== undefined) dbData.kerf_enabled = settings.kerfEnabled;
  if (settings.kerfDistance !== undefined) dbData.kerf_distance = settings.kerfDistance;
  if (settings.density !== undefined) dbData.density = settings.density;
  if (settings.scanMode !== undefined) dbData.scan_mode = settings.scanMode;
  if (settings.lowerFocusEnabled !== undefined) dbData.lower_focus_enabled = settings.lowerFocusEnabled;
  if (settings.lowerFocusValue !== undefined) dbData.lower_focus_value = settings.lowerFocusValue;
  if (settings.lightSource !== undefined) dbData.light_source = settings.lightSource;

  if (settings.bitmapEngraveMode !== undefined) dbData.bitmap_engrave_mode = settings.bitmapEngraveMode;
  if (settings.dotDuration !== undefined) dbData.dot_duration = settings.dotDuration;
  if (settings.enableBreakPoint !== undefined) dbData.enable_break_point = settings.enableBreakPoint;
  if (settings.breakPointSize !== undefined) dbData.break_point_size = settings.breakPointSize;
  if (settings.breakPointCount !== undefined) dbData.break_point_count = settings.breakPointCount;
  if (settings.breakPointMode !== undefined) dbData.break_point_mode = settings.breakPointMode;
  if (settings.breakPointDistance !== undefined) dbData.break_point_distance = settings.breakPointDistance;
  if (settings.breakPointPower !== undefined) dbData.break_point_power = settings.breakPointPower;

  if (settings.zOffset !== undefined) dbData.z_offset = settings.zOffset;
  if (settings.zPerPass !== undefined) dbData.z_per_pass = settings.zPerPass;
  if (settings.frequency !== undefined) dbData.frequency = settings.frequency;
  if (settings.overrideFrequency !== undefined) dbData.override_frequency = settings.overrideFrequency;
  if (settings.ppi !== undefined) dbData.ppi = settings.ppi;
  if (settings.enablePpi !== undefined) dbData.enable_ppi = settings.enablePpi;
  if (settings.enableLaser1 !== undefined) dbData.enable_laser1 = settings.enableLaser1;
  if (settings.enableLaser2 !== undefined) dbData.enable_laser2 = settings.enableLaser2;
  if (settings.startDelay !== undefined) dbData.start_delay = settings.startDelay;
  if (settings.endDelay !== undefined) dbData.end_delay = settings.endDelay;
  if (settings.airAssist !== undefined) dbData.air_assist = settings.airAssist;
  if (settings.blowerSpeedPercent !== undefined) dbData.blower_speed_percent = settings.blowerSpeedPercent;
  if (settings.enableCutThrough !== undefined) dbData.enable_cut_through = settings.enableCutThrough;
  if (settings.cutThroughPower !== undefined) dbData.cut_through_power = settings.cutThroughPower;
  if (settings.overcut !== undefined) dbData.overcut = settings.overcut;
  if (settings.rampLength !== undefined) dbData.ramp_length = settings.rampLength;
  if (settings.enablePerforation !== undefined) dbData.enable_perforation = settings.enablePerforation;
  if (settings.perforationCutLength !== undefined) dbData.perforation_cut_length = settings.perforationCutLength;
  if (settings.perforationSkipLength !== undefined) dbData.perforation_skip_length = settings.perforationSkipLength;
  if (settings.enableDotMode !== undefined) dbData.enable_dot_mode = settings.enableDotMode;
  if (settings.dotTime !== undefined) dbData.dot_time = settings.dotTime;
  if (settings.dotSpacing !== undefined) dbData.dot_spacing = settings.dotSpacing;
  if (settings.leadInType !== undefined) dbData.lead_in_type = settings.leadInType;
  if (settings.leadInLength !== undefined) dbData.lead_in_length = settings.leadInLength;
  if (settings.leadInAngle !== undefined) dbData.lead_in_angle = settings.leadInAngle;
  if (settings.leadOutType !== undefined) dbData.lead_out_type = settings.leadOutType;
  if (settings.leadOutLength !== undefined) dbData.lead_out_length = settings.leadOutLength;
  if (settings.leadOutAngle !== undefined) dbData.lead_out_angle = settings.leadOutAngle;
  if (settings.scanAngle !== undefined) dbData.scan_angle = settings.scanAngle;
  if (settings.crossHatch !== undefined) dbData.cross_hatch = settings.crossHatch;
  if (settings.floodFill !== undefined) dbData.flood_fill = settings.floodFill;
  if (settings.overscanPercent !== undefined) dbData.overscan_percent = settings.overscanPercent;
  if (settings.hide !== undefined) dbData.hide = settings.hide;
  if (settings.lbrnPriority !== undefined) dbData.lbrn_priority = settings.lbrnPriority;

  return dbData;
}

function mapSettingsFromDb(data: any): ShapeProcessSettings {
  const settings: ShapeProcessSettings = {
    id: data.id,
    shapeId: data.shape_id,
    operation: data.operation,
    outputEnabled: data.output_enabled || false,
    power: data.power ? parseFloat(data.power) : undefined,
    speed: data.speed ? parseFloat(data.speed) : undefined,
    passes: data.passes || 1,
    kerfEnabled: data.kerf_enabled || false,
    kerfDistance: data.kerf_distance ? parseFloat(data.kerf_distance) : undefined,
    density: data.density ? parseFloat(data.density) : undefined,
    scanMode: data.scan_mode || undefined,
    lowerFocusEnabled: data.lower_focus_enabled || false,
    lowerFocusValue: data.lower_focus_value as 1 | 2 | undefined,
    lightSource: data.light_source || undefined,
  };

  if (data.bitmap_engrave_mode) settings.bitmapEngraveMode = data.bitmap_engrave_mode;
  if (data.dot_duration) settings.dotDuration = parseFloat(data.dot_duration);
  if (data.enable_break_point !== undefined) settings.enableBreakPoint = data.enable_break_point;
  if (data.break_point_size) settings.breakPointSize = parseFloat(data.break_point_size);
  if (data.break_point_count) settings.breakPointCount = data.break_point_count;
  if (data.break_point_mode) settings.breakPointMode = data.break_point_mode;
  if (data.break_point_distance) settings.breakPointDistance = parseFloat(data.break_point_distance);
  if (data.break_point_power) settings.breakPointPower = parseFloat(data.break_point_power);

  if (data.z_offset) settings.zOffset = parseFloat(data.z_offset);
  if (data.z_per_pass) settings.zPerPass = parseFloat(data.z_per_pass);
  if (data.frequency) settings.frequency = parseFloat(data.frequency);
  if (data.override_frequency !== undefined) settings.overrideFrequency = data.override_frequency;
  if (data.ppi) settings.ppi = parseFloat(data.ppi);
  if (data.enable_ppi !== undefined) settings.enablePpi = data.enable_ppi;
  if (data.enable_laser1 !== undefined) settings.enableLaser1 = data.enable_laser1;
  if (data.enable_laser2 !== undefined) settings.enableLaser2 = data.enable_laser2;
  if (data.start_delay) settings.startDelay = parseFloat(data.start_delay);
  if (data.end_delay) settings.endDelay = parseFloat(data.end_delay);
  if (data.air_assist !== undefined) settings.airAssist = data.air_assist;
  if (data.blower_speed_percent) settings.blowerSpeedPercent = parseFloat(data.blower_speed_percent);
  if (data.enable_cut_through !== undefined) settings.enableCutThrough = data.enable_cut_through;
  if (data.cut_through_power) settings.cutThroughPower = parseFloat(data.cut_through_power);
  if (data.overcut) settings.overcut = parseFloat(data.overcut);
  if (data.ramp_length) settings.rampLength = parseFloat(data.ramp_length);
  if (data.enable_perforation !== undefined) settings.enablePerforation = data.enable_perforation;
  if (data.perforation_cut_length) settings.perforationCutLength = parseFloat(data.perforation_cut_length);
  if (data.perforation_skip_length) settings.perforationSkipLength = parseFloat(data.perforation_skip_length);
  if (data.enable_dot_mode !== undefined) settings.enableDotMode = data.enable_dot_mode;
  if (data.dot_time) settings.dotTime = parseFloat(data.dot_time);
  if (data.dot_spacing) settings.dotSpacing = parseFloat(data.dot_spacing);
  if (data.lead_in_type) settings.leadInType = data.lead_in_type;
  if (data.lead_in_length) settings.leadInLength = parseFloat(data.lead_in_length);
  if (data.lead_in_angle) settings.leadInAngle = parseFloat(data.lead_in_angle);
  if (data.lead_out_type) settings.leadOutType = data.lead_out_type;
  if (data.lead_out_length) settings.leadOutLength = parseFloat(data.lead_out_length);
  if (data.lead_out_angle) settings.leadOutAngle = parseFloat(data.lead_out_angle);
  if (data.scan_angle) settings.scanAngle = parseFloat(data.scan_angle);
  if (data.cross_hatch !== undefined) settings.crossHatch = data.cross_hatch;
  if (data.flood_fill !== undefined) settings.floodFill = data.flood_fill;
  if (data.overscan_percent) settings.overscanPercent = parseFloat(data.overscan_percent);
  if (data.hide !== undefined) settings.hide = data.hide;
  if (data.lbrn_priority) settings.lbrnPriority = parseFloat(data.lbrn_priority);

  return settings;
}
